import store from '@/store';
import S_factorySystemClass from '@/service/api/v1/factorySystemClass';

function getScopeUserFactorySystemSubclasses() {
  const state = store.state;
  const currentUser = state.stone_auth?.currentUser;
  const currentFactoryId = state.app?.factory?.id;

  const factoryScope =
    currentUser?.user_factory_system_subclasses?.find(
      ({ factory }) => factory?.id === currentFactoryId,
    ) || {};

  const roles = currentUser?.user_factory_roles?.filter(
    ({ factory }) => factory?.id === currentFactoryId,
  ) || [];

  const system_subclasses = [...(factoryScope.system_subclasses || [])];
  const factory_system_subclasses = [
    ...(factoryScope.factory_system_subclasses || []),
  ];

  for (const role of roles) {
    if (role?.system_subclasses?.length) {
      system_subclasses.push(...role.system_subclasses);
    }

    if (role?.factory_system_subclasses?.length) {
      factory_system_subclasses.push(
        ...role.factory_system_subclasses,
      );
    }
  }

  return {
    system_subclasses,
    factory_system_subclasses,
  };
}

function mapSystemClassRows(rows, childKey, scope = [], redNoScopeSubClass = true) {
  const scopeSet = new Set(scope.map(item => item.id));
  return (rows || []).map(row => ({
    id: row.id,
    title: row.name,
    items: (row[childKey] || []).map(sub => ({
      id: sub.id,
      title: sub.name,
      color: scopeSet.has(sub.id) ? undefined : redNoScopeSubClass ? 'red' : undefined,
      items: [],
    })),
  }));
}

function mapSystemClassRowsForStateDropdownSelectClassification(rows, childKey) {
  return (rows || []).map(row => ({
    title: row.name,
    value: row.id,
    menu: (row[childKey] || []).map(sub => ({
      text: sub.name,
      value: sub.id,
    })),
  }));
}

function countFactorySystemSubclassLeaves(rows) {
  return (rows || []).flatMap(row => row.factory_system_classes || []).length;
}

function countFactorySystemSubclassLeavesFromFilterFields(filterFields) {
  const field = (filterFields || []).find(item => item.name === 'factorySystemClasses');
  if (!field?.items?.length) return 0;
  return field.items.reduce((sum, row) => sum + (row.menu?.length || 0), 0);
}

function isSubclassFilterFieldName(name) {
  return name === 'systemSubclasses' || name === 'factorySystemClasses';
}

function getLeafIdsFromClassificationMenu(menu) {
  return (menu || []).flatMap(row => (row.menu || []).map(item => item.value));
}

function toSubclassFilterApiParam(selectedIds, totalCount) {
  const len = selectedIds?.length ?? 0;
  if (len === 0 || len === totalCount) return undefined;
  return selectedIds.join();
}

function intersectSubclassSelection(selectedIds, menu) {
  const allIds = getLeafIdsFromClassificationMenu(menu);
  if (!allIds.length) return [];
  const allowed = new Set(allIds);
  const intersection = (selectedIds || []).filter(id => allowed.has(id));
  if (intersection.length === 0) return allIds;
  return intersection;
}

function resolveSubclassSelectionFromQuery(queryValue, menu, allIdReturnEmpty = false) {
  const allIds = getLeafIdsFromClassificationMenu(menu);
  if (!queryValue) return allIdReturnEmpty ? [] : allIds;
  const queryIds = String(queryValue)
    .split(',')
    .filter(Boolean);
  if (!allIds.length) return [];
  const allowed = new Set(allIds);
  const intersection = queryIds.filter(id => allowed.has(id));
  if (intersection.length === 0) return allIdReturnEmpty ? [] : allIds;
  return intersection;
}

function buildSubclassQueryValue(selectedIds, menu) {
  const totalCount = getLeafIdsFromClassificationMenu(menu).length;
  return toSubclassFilterApiParam(selectedIds, totalCount);
}

function applySubclassFieldsToRouteQuery(baseQuery, fields) {
  const query = { ...baseQuery };
  (fields || []).forEach(({ queryKey, selectedIds, menu }) => {
    const value = buildSubclassQueryValue(selectedIds, menu);
    if (value !== undefined) {
      query[queryKey] = value;
    } else {
      delete query[queryKey];
    }
  });
  return query;
}

function mapDashboardSystemClassMenu({ factorySystemClasses, systemClasses }) {
  const menu = [];
  if ((factorySystemClasses || []).length) {
    menu.push({
      id: 'factorySystemClass',
      title: '自訂領域',
      items: factorySystemClasses.map(row => ({
        id: row.id,
        title: row.name,
        items: [],
      })),
    });
  }
  if ((systemClasses || []).length) {
    menu.push({
      id: 'systemClass',
      title: '預設領域',
      items: systemClasses.map(row => ({
        id: row.id,
        title: row.name,
        items: [],
      })),
    });
  }
  return menu;
}

async function fetchFactorySystemClassMenuForClassification() {
  try {
    const res = await S_factorySystemClass.indexFactory({}, false);
    return mapSystemClassRowsForStateDropdownSelectClassification(
      res,
      'factory_system_classes',
    );
  } catch (error) {
    return [];
  }
}

async function fetchMultiSystemClassMenu({ systemClasses, factorySystemClasses } = {}, redNoScopeSubClass = true) {
  try {
    const res = factorySystemClasses != null
      ? factorySystemClasses
      : await S_factorySystemClass.indexFactory({}, false);
    const {system_subclasses, factory_system_subclasses} = getScopeUserFactorySystemSubclasses();
    return [
      {
        id: 'factorySystemClass',
        title: '自訂領域',
        items: mapSystemClassRows(res, 'factory_system_classes', factory_system_subclasses, redNoScopeSubClass),
      },
      {
        id: 'systemClass',
        title: '預設領域',
        items: mapSystemClassRows(systemClasses, 'system_subclasses', system_subclasses, redNoScopeSubClass),
      },
    ];
  } catch (error) {
    alert(error);
    return [];
  }
}

function buildLeafToClassMap(treeRows, childKey) {
  const map = new Map();
  (treeRows || []).forEach(row => {
    (row[childKey] || []).forEach(leaf => map.set(leaf.id, row));
  });
  return map;
}

// 只有 API classes 中的領域會成群組；樹僅用於子領域歸屬對應，禁止反查補領域
function groupOneDomain({ classes, subclasses, treeRows, childKey }) {
  const leafToClassMap = buildLeafToClassMap(treeRows, childKey);
  const groupByClassId = new Map();
  const orphans = [];
  (classes || []).forEach(apiClass => {
    groupByClassId.set(apiClass.id, { classItem: apiClass, subclasses: [] });
  });
  (subclasses || []).forEach(subclass => {
    const parentRow = leafToClassMap.get(subclass.id);
    if (parentRow && groupByClassId.has(parentRow.id)) {
      groupByClassId.get(parentRow.id).subclasses.push(subclass);
    } else {
      orphans.push(subclass);
    }
  });
  return { groups: [...groupByClassId.values()], orphans };
}

// 自訂領域群組在前、預設領域在後；父領域已刪除的子領域（orphans）附於最末
function groupMultiSystemClasses(apiFields, { factorySystemClassRows = [], systemClassRows } = {}) {
  const {
    system_classes,
    system_subclasses,
    factory_system_classes,
    factory_system_subclasses,
  } = apiFields || {};
  const factory = groupOneDomain({
    classes: factory_system_classes,
    subclasses: factory_system_subclasses,
    treeRows: factorySystemClassRows,
    childKey: 'factory_system_classes',
  });
  const system = groupOneDomain({
    classes: system_classes,
    subclasses: system_subclasses,
    treeRows: systemClassRows || store.state.model.systemClasses || [],
    childKey: 'system_subclasses',
  });
  return {
    groups: [...factory.groups, ...system.groups],
    orphans: [...factory.orphans, ...system.orphans],
  };
}

// ContentChipsList 格式（{name, icon(url), leadingIcon, bold}），供 Show 頁 ListCardItem type="chip"
// options.color 統一上在領域與子領域（含 orphan）chip
function formatMultiSystemClassContentChips(apiFields, options) {
  const { color } = options || {};
  const { groups, orphans } = groupMultiSystemClasses(apiFields, options);
  const toSubclassChip = subclass => {
    const chip = { ...subclass };
    if (!chip.icon) chip.leadingIcon = 'icon-enterprise';
    if (color) chip.color = color;
    return chip;
  };
  const chips = [];
  groups.forEach(({ classItem, subclasses }) => {
    chips.push({
      ...classItem,
      bold: true,
      leadingIcon: classItem.icon ? undefined : 'icon-enterprise',
      ...(color ? { color } : {}),
    });
    chips.push(...subclasses.map(toSubclassChip));
  });
  chips.push(...orphans.map(toSubclassChip));
  return chips;
}

// WsChips001 格式（{text, image(url), icon(font class), bold}），供 Index 卡片 tags
function formatMultiSystemClassCardTags(apiFields, options) {
  const { groups, orphans } = groupMultiSystemClasses(apiFields, options);
  const toSubclassTag = subclass => ({
    text: subclass.name,
    image: subclass.icon || '',
    icon: subclass.icon ? '' : 'icon-enterprise',
    isWrap: true,
  });
  const tags = [];
  groups.forEach(({ classItem, subclasses }) => {
    tags.push({
      text: classItem.name,
      image: classItem.icon || '',
      icon: classItem.icon ? '' : 'icon-enterprise',
      isWrap: true,
      bold: true,
    });
    tags.push(...subclasses.map(toSubclassTag));
  });
  tags.push(...orphans.map(toSubclassTag));
  return tags;
}

export default {
  mapSystemClassRows,
  mapDashboardSystemClassMenu,
  fetchFactorySystemClassMenuForClassification,
  fetchMultiSystemClassMenu,
  mapSystemClassRowsForStateDropdownSelectClassification,
  countFactorySystemSubclassLeaves,
  countFactorySystemSubclassLeavesFromFilterFields,
  isSubclassFilterFieldName,
  getLeafIdsFromClassificationMenu,
  toSubclassFilterApiParam,
  intersectSubclassSelection,
  resolveSubclassSelectionFromQuery,
  buildSubclassQueryValue,
  applySubclassFieldsToRouteQuery,
  getScopeUserFactorySystemSubclasses,
  groupMultiSystemClasses,
  formatMultiSystemClassContentChips,
  formatMultiSystemClassCardTags,
};
